import React, {
  useMemo,
  useState,
  useEffect,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Span,
  Button,
  Paragraph,
  PanelSlider,
  LoadingContainer,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { useQuery } from '@sopranodesign/pulse-hooks';
import { PermissionFilter } from '@sopranodesign/pulse-auth';

import FolderTable from './FolderTable';
import Resource from './Resource';

dayjs.extend(utc);
dayjs.extend(relativeTime);

const SiteViewPanel = ({ state }) => {
  const {
    crawlFolders,
    selectedSite,
    refetchStatus,
    setCrawlSites,
    setActiveModal,
    setIsPanelOpen,
    setCrawlFolders,
    setSelectedSite,
    setTokenInvalid,
    setCrawlResources,
    setSelectedFolder,
    setSelectedActionSite
  } = state;

  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [selectedLocalFolder, setSelectedLocalFolder] = useState(null);

  const order = useOrderableTableReducer({ id: 'folders' });
  const pagination = usePaginationReducer({ id: 'folders' });

  const queryKey = useMemo(
    () => [
      `generative-ai/sharepoint/site/${selectedSite.id}`,
      { ...pagination.params, ...order.params }
    ],
    [selectedSite, pagination.params, order.params]
  );

  const {
    error,
    isLoading,
    data: folderData
  } = useQuery({
    queryKey,
    refetchInterval: shouldRefetch ? 5000 : false,
    enabled: !!selectedSite && crawlFolders
  });

  // Handle refetch logic
  useEffect(() => {
    if (!isLoading && folderData?.has_pending !== undefined) {
      setShouldRefetch(folderData.has_pending);
    }
  }, [folderData?.has_pending, isLoading]);

  // Handle token invalid error
  useEffect(() => {
    if (error) {
      const message = error?.response?.data?.message || error?.message;
      if (message === 'Token is invalid, please reconnect.') {
        setIsPanelOpen(false);
        setSelectedSite(null);
        setActiveModal(null);
        setTokenInvalid(true);
      }
    }
  }, [error, setSelectedSite, setIsPanelOpen, setTokenInvalid, setActiveModal]);

  const handleAddFolder = useCallback(() => {
    setActiveModal('addFolder');
  }, [setActiveModal]);

  const handleSyncSite = useCallback(() => {
    setSelectedActionSite(selectedSite);
    setActiveModal('resyncSite');
  }, [selectedSite, setActiveModal, setSelectedActionSite]);

  const handleViewFolder = useCallback((folder) => {
    setSelectedLocalFolder(folder);
    setSelectedFolder(folder);
    setCrawlSites(false);
    setCrawlFolders(false);
    setCrawlResources(true);
  }, [setCrawlSites, setCrawlFolders, setCrawlResources, setSelectedFolder, setSelectedLocalFolder]);

  const handleCloseFolderPanel = useCallback(() => {
    setSelectedLocalFolder(null);

    // Return to folder list polling
    setCrawlSites(false);
    setCrawlFolders(true);
    setCrawlResources(false);
  }, [setCrawlSites, setCrawlFolders, setCrawlResources, setSelectedLocalFolder]);

  const handleCloseSitePanel = useCallback(() => {
    setIsPanelOpen(false);
    setActiveModal(null);
    refetchStatus();
    setCrawlSites(true);
    setCrawlFolders(false);
    setCrawlResources(false);
  }, [refetchStatus, setCrawlSites, setActiveModal, setIsPanelOpen, setCrawlFolders, setCrawlResources]);

  const renderActionButtons = useMemo(() => (
    <>
      <PermissionFilter can="edit generative ai resource">
        <Flex center right flex mt>
          <Button
            ml
            icon="plus"
            onClick={handleAddFolder}
            isLoading={isLoading}
          >
            Add folder
          </Button>
          {folderData?.data?.length > 0 &&
            <Button
              ml
              icon="refresh"
              onClick={handleSyncSite}
              isLoading={isLoading}
            >
              Sync All
            </Button>
          }
        </Flex>
      </PermissionFilter>
      <Flex center right mt>
        <Span
          light
          tinyText
          title={
            selectedSite?.last_sync_at
              ? `${dayjs.utc(selectedSite?.last_sync_at).format('YYYY-MM-DD HH:mm:ss')} UTC`
              : 'No previous sync'
          }
        >
          Last sync {selectedSite?.last_sync_at ? dayjs.utc(selectedSite?.last_sync_at).from(dayjs.utc()) : 'never'}
        </Span>
      </Flex>
    </>
  ), [isLoading, handleSyncSite, handleAddFolder, folderData?.data?.length, selectedSite?.last_sync_at]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex col center middle tall>
          <LoadingContainer />
          <Paragraph mt>Loading...</Paragraph>
        </Flex>
      );
    }

    if (error) {
      return (
        <p style={{ color: 'red' }}>
          Failed to fetch folders: {error.message || 'Unknown error occurred'}
        </p>
      );
    }

    if (folderData?.data?.length > 0) {
      return (
        <>
          <FolderTable
            {...folderData}
            has_pending={folderData.has_pending}
            site={selectedSite}
            orderProps={order.props}
            onSyncFolder={(folder) => {
              setSelectedFolder(folder);
              setActiveModal('resyncFolder');
            }}
            onDeleteFolder={(folder) => {
              setSelectedFolder(folder);
              setActiveModal('deleteFolder');
            }}
            onViewFolder={handleViewFolder}
          />
          {renderActionButtons}
        </>
      );
    }

    return (
      <>
        <p>No data available for this site.</p>
        {renderActionButtons}
      </>
    );
  };

  return (
    <AnimatePresence>
      {selectedSite && (
        <PanelSlider
          key={1}
          header={`Site: ${selectedSite.site_name}`}
          handleHide={handleCloseSitePanel}
        >
          <Flex col>{renderContent()}</Flex>
        </PanelSlider>
      )}

      {selectedLocalFolder && selectedSite && (
        <PanelSlider
          key={2}
          header={`Site: ${selectedSite.site_name}, Folder: ${selectedLocalFolder.name}`}
          handleHide={handleCloseFolderPanel}
        >
          <Resource site={selectedSite} folder={selectedLocalFolder} state={state} />
        </PanelSlider>
      )}
    </AnimatePresence>
  );
};

SiteViewPanel.propTypes = {
  site: PropTypes.shape({
    id: PropTypes.number.isRequired,
    site_name: PropTypes.string.isRequired,
    last_sync_at: PropTypes.string
  }).isRequired,
  state: PropTypes.shape({
    crawlFolders: PropTypes.bool.isRequired,
    queryClient: PropTypes.object.isRequired,
    refetchFolder: PropTypes.func.isRequired,
    refetchStatus: PropTypes.func.isRequired,
    setCrawlSites: PropTypes.func.isRequired,
    selectedSite: PropTypes.object.isRequired,
    setActiveModal: PropTypes.func.isRequired,
    setIsPanelOpen: PropTypes.func.isRequired,
    setCrawlFolders: PropTypes.func.isRequired,
    setSelectedSite: PropTypes.func.isRequired,
    setTokenInvalid: PropTypes.func.isRequired,
    setCrawlResources: PropTypes.func.isRequired,
    setSelectedFolder: PropTypes.func.isRequired,
    setSelectedActionSite: PropTypes.func.isRequired
  }).isRequired,
};

export default SiteViewPanel;